import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
// Sentry deferred — RC bundle exceeded Jenkins agent RAM. Will return
// in a dedicated PR with lazy-loaded Sentry.
// Import API config early to configure axios defaults
import "./core/config/api.js";
// Re-applies any persisted X-Active-Store header before the first request
// goes out (Phase 5 of the multi-store access rework) — see the file for why.
import "./core/config/activeStore.js";
// Install the app-wide error safety net (breadcrumbs + window.onerror +
// unhandledrejection → /api/log/errors) as early as possible so failures
// during bootstrap are still captured. Best-effort: never throws.
import { installGlobalErrorHandlers } from "./core/errorReporter.js";
installGlobalErrorHandlers();
// Rolling console + failed-request buffer for the beta error button.
import { installDiagnosticsBuffer } from "./core/diagnosticsBuffer.js";
installDiagnosticsBuffer();
// DevTools deterrent for everyone but master admins — see the file for its limits.
import { startDevtoolsGuard } from "./core/devtoolsGuard.js";
startDevtoolsGuard();
// Google Analytics 4 — no-op until VITE_GA_MEASUREMENT_ID is set (.env.production).
import { initGA4 } from "./core/analytics/ga4.js";
initGA4();
import App from "./App.jsx";
import ErrorBoundary from "./layouts/ErrorBoundary.jsx";

// Unregister any service workers left over from the old monolith deployment
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => registration.unregister());
  });
}
import "./index.css";
// Design tokens are GENERATED from tokens/rc.tokens.json (npm run tokens) — never hand-edit tokens.css.
import "./styles/fonts.css";
import "./styles/tokens.css";
import { queryParam } from "./core/utils/queryParam.js";

// Suppress console warnings from third-party libraries that we can't fix
const originalWarn = console.warn;
const originalError = console.error;

const shouldSuppressWarning = (message) => {
  // Convert message to lowercase for case-insensitive matching
  const lowerMessage = message.toLowerCase();
  
  return (
    // Provider2 defaultProps warning from react-alert
    (lowerMessage.includes('provider2') && lowerMessage.includes('defaultprops')) ||
    (lowerMessage.includes('support for defaultprops') && lowerMessage.includes('function components')) ||
    // UNSAFE lifecycle method warnings - catch all variations
    lowerMessage.includes('unsafe_componentwillmount') ||
    lowerMessage.includes('unsafe_componentwillreceiveprops') ||
    lowerMessage.includes('componentwillreceiveprops') ||
    lowerMessage.includes('componentwillmount') ||
    // Component-specific warnings from updated libraries
    lowerMessage.includes('infinitescroll2') ||
    lowerMessage.includes('select2') ||
    // Catch "Please update the following components" messages
    (lowerMessage.includes('please update the following components') && 
     (lowerMessage.includes('infinitescroll') || lowerMessage.includes('select'))) ||
    // Catch strict mode warnings
    (lowerMessage.includes('strict mode') && 
     (lowerMessage.includes('componentwill') || lowerMessage.includes('unsafe'))) ||
    // Legacy context API warnings
    (lowerMessage.includes('legacy context api') && lowerMessage.includes('strict-mode')) ||
    lowerMessage.includes('sortablelist') ||
    // findDOMNode deprecation warnings
    lowerMessage.includes('finddomnode is deprecated') ||
    lowerMessage.includes('finddomnode') ||
    // Failed prop type warnings from third-party libraries
    (lowerMessage.includes('failed prop type') && 
     (lowerMessage.includes('reactswitch') || lowerMessage.includes('react-switch')))
  );
};

console.warn = (...args) => {
  // Convert all arguments to strings for checking
  const message = args.map(arg => 
    typeof arg === 'string' ? arg : 
    typeof arg === 'object' && arg !== null ? JSON.stringify(arg) : 
    String(arg)
  ).join(' ');
  
  if (shouldSuppressWarning(message)) {
    // Suppress these warnings - they're from third-party libraries we can't fix
    return;
  }
  originalWarn.apply(console, args);
};

// Also suppress console.error for React warnings (some React warnings use console.error)
console.error = (...args) => {
  const message = args.map(arg => 
    typeof arg === 'string' ? arg : 
    typeof arg === 'object' && arg !== null ? JSON.stringify(arg) : 
    String(arg)
  ).join(' ');
  
  // Only suppress React warnings, not actual errors
  if (shouldSuppressWarning(message) && 
      (message.includes('Warning:') || message.includes('React'))) {
    return;
  }
  originalError.apply(console, args);
};

// Get root element
const rootElement = document.getElementById("app");
if (!rootElement) {
  throw new Error("Root element '#app' not found in DOM");
}

// Expose queryParam utility globally (for legacy compatibility)
// TODO: Consider removing this and using proper imports instead
if (typeof window !== "undefined") {
  window.queryParam = queryParam;
}

// Create React root
const root = createRoot(rootElement);

// Path-based bootstrap split: /pay/:token is a public, isolated customer
// surface (Stripe card entry). It mounts PayByLinkRoot WITHOUT the admin
// providers and WITHOUT StrictMode — StrictMode's double-invoke cancels the
// in-flight Stripe Elements iframes and hangs the form (same fix the DAO
// storefront uses). The dynamic import keeps /pay's bundle graph separate.
// Every other path mounts <App/> as before.
if (window.location.pathname.startsWith("/pay/")) {
  import("./payByLink/PayByLinkRoot.jsx").then(({ default: PayByLinkRoot }) => {
    // /pay has no App-level providers, so wrap it in the shared ErrorBoundary
    // directly — otherwise a render error here unmounts to a blank screen with
    // no fallback (App gets its boundary from inside App.jsx).
    root.render(
      <ErrorBoundary>
        <PayByLinkRoot />
      </ErrorBoundary>
    );
  });
} else {
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
}

// Hot Module Replacement (HMR) - Vite handles this automatically with React Fast Refresh
// The @vitejs/plugin-react enables Fast Refresh which preserves component state during updates
if (import.meta.hot) {
  // Accept HMR updates for this module and all its dependencies
  import.meta.hot.accept();
}
